import type { SxProps } from '@mui/system';
import type { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import type { Theme } from '@mui/joy';
import { IPortfolioDefinitionSubType, IPortfolioDefinitionType } from '../../../../generated/graphql.tsx';
import type { PortfolioDefActionDef } from './PortfolioDefinitionActions.tsx';
import type { ReactElement, ReactNode } from 'react';
import type { NavigateFunction } from 'react-router';
import { DialogMenuItem } from 'components/technical/DialogDropdown/DialogMenuItem.tsx';
import { Add } from '@mui/icons-material';
import { MenuItem } from '@mui/joy';
import { ListItemDecorator } from '@mui/joy';
import CreateUpdatePortfolioDialog from './update/CreateUpdatePortfolioDialog.tsx';
import type { CreateAccountIdAutocompleteOptionsInputAccount } from 'components/portfolio/account/AccountService.tsx';
import type { SubfundAutocompleteOptionInput } from 'components/portfolio/fund/SubFundService.tsx';
import DonutLargeOutlinedIcon from '@mui/icons-material/DonutLargeOutlined';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { UploadRebalancePortfoliosReturns } from './rebalancedPortfolio/UploadRebalancePortfoliosReturns.tsx';
import sortBy from 'lodash/fp/sortBy';
import dayjs from 'dayjs';

export const selectableItemsClass = 'genie-portfolioDefList-item';

export const calculateItemProps = ({
  defId,
  selected,
  showNoRulesWarning,
}: { defId: string; selected: boolean; showNoRulesWarning: boolean }): {
  className: string;
  cardSx: SxProps<Theme>;
  dataset: Record<string, string>;
  selectable: boolean;
} => {
  return {
    className: showNoRulesWarning ? '' : selectableItemsClass,
    dataset: {
      id: defId,
    },
    selectable: !showNoRulesWarning,
    cardSx: (theme: Theme): SystemStyleObject<Theme> => ({
      border: `2px solid ${selected && !showNoRulesWarning ? theme.palette.primary[500] : 'transparent'}`,
      '& > *': {
        userSelect: 'none',
      },
    }),
  };
};

export const getPortfolioRebalancingStatus = (def: PortfolioDefActionDef): 'Rebalanced' | 'Real' => {
  if (def.type === IPortfolioDefinitionType.Real) {
    return 'Real';
  }

  return 'Rebalanced';
};

export const sortPortfolios = <T extends { name: string; createdAt: string | Date }>(portfolios: T[]): T[] => {
  return sortBy(
    [(portfolio) => portfolio.name.toLowerCase(), (portfolio) => dayjs(portfolio.createdAt).valueOf()],
    portfolios
  );
};

const IconAndText = ({ children, title }: { children: ReactNode; title: string }): ReactElement => {
  return (
    <>
      <ListItemDecorator>{children}</ListItemDecorator>
      {title}
    </>
  );
};

export const createNewPortfolioActions = ({
  navigate,
  accounts,
  subFunds,
}: {
  navigate: NavigateFunction;
  accounts: CreateAccountIdAutocompleteOptionsInputAccount[];
  subFunds: SubfundAutocompleteOptionInput[];
}): ReactElement[] => {
  return [
    <DialogMenuItem
      key="create-new-real-portfolio"
      renderDialog={({ onClose }): ReactElement => (
        <CreateUpdatePortfolioDialog
          onClose={onClose}
          onCompleted={onClose}
          def={{
            subType: IPortfolioDefinitionSubType.Default,
            type: IPortfolioDefinitionType.Real,
            realDefAccounts: [],
            realDefSubFunds: [],
          }}
          multifactors={[]}
          accounts={accounts}
          subFunds={subFunds}
        />
      )}
    >
      <ListItemDecorator>
        <Add />
      </ListItemDecorator>
      Link GenieAI PMS accounts
    </DialogMenuItem>,
    <MenuItem
      key="define-portfolio-composition"
      onClick={() => {
        navigate('/app/copilot/portfolio-builder');
      }}
    >
      <IconAndText title={'Define portfolio composition'}>
        <DonutLargeOutlinedIcon fontSize="md" />
      </IconAndText>
    </MenuItem>,

    <MenuItem
      key="define-multifactor-rule"
      onClick={() => {
        navigate('/app/market/asset-screener');
      }}
    >
      <IconAndText title={'Define multifactor rule'}>
        <DonutLargeOutlinedIcon fontSize="md" />
      </IconAndText>
    </MenuItem>,
    <MenuItem
      key="define-portfolio-composition-with-optimizer-and-asset-universe"
      onClick={() => {
        navigate('/app/copilot/portfolio-optimizer/optimizations/portfolio-definition-with-asset-universe');
      }}
    >
      <IconAndText title={'Define composition with optimizer and asset universe'}>
        <DonutLargeOutlinedIcon fontSize="md" />
      </IconAndText>
    </MenuItem>,
    <MenuItem
      key="define-portfolio-composition-with-optimizer-and-asset-screener"
      onClick={() => {
        navigate('/app/copilot/portfolio-optimizer/optimizations/portfolio-definition-with-asset-screener');
      }}
    >
      <IconAndText title={'Define composition with optimizer and asset screener'}>
        <DonutLargeOutlinedIcon fontSize="md" />
      </IconAndText>
    </MenuItem>,
    <DialogMenuItem
      key="import-positions"
      renderDialog={({ onClose }): ReactElement => (
        <CreateUpdatePortfolioDialog
          onClose={onClose}
          onCompleted={(defId) => {
            navigate(`/app/copilot/lab/portfolio/${defId}/import-positions`);
            onClose();
          }}
          def={{
            subType: IPortfolioDefinitionSubType.PositionBased,
            type: IPortfolioDefinitionType.Rebalanced,
            realDefAccounts: [],
            realDefSubFunds: [],
            rebalancedDetails: {
              defaultAssetComposition: [],
              transactionTag: '',
            },
          }}
          multifactors={[]}
          accounts={[]}
          subFunds={[]}
        />
      )}
    >
      <IconAndText title={'Upload portfolio composition history'}>
        <BackupOutlinedIcon fontSize="md" />
      </IconAndText>
    </DialogMenuItem>,
    <DialogMenuItem
      key="use-existing-transactions"
      renderDialog={({ onClose }): ReactElement => (
        <CreateUpdatePortfolioDialog
          onClose={onClose}
          onCompleted={() => {
            onClose();
          }}
          def={{
            subType: IPortfolioDefinitionSubType.TransactionBased,
            type: IPortfolioDefinitionType.Rebalanced,
            realDefAccounts: [],
            realDefSubFunds: [],
            rebalancedDetails: {
              defaultAssetComposition: [],
              transactionTag: '',
            },
          }}
          multifactors={[]}
          accounts={[]}
          subFunds={[]}
        />
      )}
    >
      <IconAndText title={'Use existing transactions'}>
        <SwapHorizIcon fontSize="md" />
      </IconAndText>
    </DialogMenuItem>,
    <DialogMenuItem
      key="upload-returns"
      renderDialog={({ onClose }): ReactElement => (
        <UploadRebalancePortfoliosReturns onClose={onClose} onAdded={onClose} />
      )}
    >
      <IconAndText title={'Upload returns'}>
        <BackupOutlinedIcon fontSize="md" />
      </IconAndText>
    </DialogMenuItem>,
  ].filter((el) => !!el);
};
